import {type DataTestProps, getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {
  Anchor,
  Box,
  Button,
  CloseButton,
  Collapse,
  createStyles,
  CSSObject,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Transition,
  UnstyledButton,
} from '@mantine/core';
import {useClickOutside, useMediaQuery} from '@mantine/hooks';
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import navigateToURL from 'lib/navigateToURL';
import {ChatSettingsTab} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {sortForDiscovery, useUsedChatUseCaseIds} from 'llm/chat-library/use-cases/chatUseCaseUsage';
import recordChatUseCaseRun from 'llm/chat-library/use-cases/recordChatUseCaseRun';
import {chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  countUntriedCategories,
  pickIntroProgressiveDisclosureCategories,
  ProgressiveDisclosureCategory,
} from './ProgressiveDisclosureData';

interface ProgressiveDisclosureProps {
  isStacked?: boolean;
  isVisible?: boolean;
  /** Skip the exit animation when the containing intro is hard-swapping layouts. */
  hideImmediately?: boolean;
  onExitComplete?: () => void;
  preserveHiddenGeometry?: boolean;
  onHoverOption: (prompt: string | null) => void;
  /**
   * Submits the prompt as a new chat. Resolving `true` means the chat was actually created; `false`
   * or nothing means submission never happened, so the option must not be recorded as tried.
   */
  onSelectOption: (prompt: string) => Promise<boolean> | void;
}

const DISCLOSURE_ITEM_EXIT_MS = 40;
const DISCLOSURE_ITEM_EXIT_STAGGER_MS = 15;
/** @visibleForTesting */
export const DISCLOSURE_ITEM_ENTER_MS = 420;
/** @visibleForTesting */
export const DISCLOSURE_ITEM_ENTER_STAGGER_MS = 70;
const DISCLOSURE_ITEM_ENTER_OFFSET_PX = 14;
const DISCLOSURE_HEIGHT_TRANSITION_MS = 180;
export const PROGRESSIVE_DISCLOSURE_EXIT_TRANSITION_MS = 100;
export const PROGRESSIVE_DISCLOSURE_ENTER_TRANSITION_MS = 220;

function preserveExitedTransitionGeometry(transitionStyles: React.CSSProperties): React.CSSProperties {
  return transitionStyles.display === 'none'
    ? {...transitionStyles, display: 'block', opacity: 0, visibility: 'hidden'}
    : transitionStyles;
}

/** @visibleForTesting */
export const DISCLOSURE_ICON_SIZE_PX = 14;

/**
 * Font Awesome sizes its glyphs from each icon's own aspect ratio, so pin an explicit square box to keep
 * every category glyph identical regardless of which icon a category uses. The doubled selector outranks
 * the Font Awesome stylesheet's own width rule.
 */
const squareIconStyles: CSSObject = {
  '&&': {
    width: DISCLOSURE_ICON_SIZE_PX,
    height: DISCLOSURE_ICON_SIZE_PX,
    fontSize: DISCLOSURE_ICON_SIZE_PX,
    flexShrink: 0,
  },
};

const useStyles = createStyles((theme, {isStacked}: {isStacked: boolean}) => ({
  container: {
    paddingTop: isStacked ? theme.spacing.lg : theme.spacing.md,
    paddingRight: isStacked ? 0 : theme.spacing.md,
    paddingBottom: isStacked ? theme.spacing.sm : theme.spacing.md,
    paddingLeft: isStacked ? 0 : theme.spacing.md,
  },
  optionItem: {
    display: 'block',
    width: '100%',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: 0,
    fontSize: theme.fontSizes.md,
    color: Color.GRAY_900,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: theme.colors.blue[0],
    },
  },
  optionsPanel: {
    borderRadius: theme.radius.lg,
    overflow: 'hidden',
    padding: theme.spacing.sm,
  },
  optionsPanelTitle: {
    color: Color.GRAY_500,
  },
  optionsPanelTitleIcon: {
    ...squareIconStyles,
    color: Color.GRAY_500,
    '& svg': {
      color: Color.GRAY_500,
    },
  },
  divider: {
    borderColor: theme.colors.gray[2],
  },
  categoryPill: {
    fontWeight: theme.other.fontWeights.normal,
    backgroundColor: theme.white,
    color: Color.GRAY_900,
    border: `1px solid ${Color.GRAY_250}`,
    '&:hover': {
      backgroundColor: Color.BLUE_100,
      borderColor: Color.BLUE_100,
      color: Color.BLUE_450,
    },
    '&:hover $categoryPillLeftIcon': {
      opacity: 1,
    },
  },
  categoryPillLeftIcon: {
    marginRight: theme.spacing.sm,
    opacity: 0.5,
  },
  categoryPillIcon: squareIconStyles,
  exploreLink: {
    fontWeight: theme.other.fontWeights.normal,
  },
}));

export default function ProgressiveDisclosure({
  isStacked = false,
  isVisible = true,
  hideImmediately = false,
  onExitComplete,
  preserveHiddenGeometry = false,
  onHoverOption,
  onSelectOption,
}: ProgressiveDisclosureProps): JSX.Element {
  const {classes} = useStyles({isStacked});
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const previousIsStackedRef = useRef(isStacked);
  const lastHoveredOptionRef = useRef<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const categoryListRef = useRef<HTMLDivElement>(null);
  const [categoryListMinHeight, setCategoryListMinHeight] = useState(0);
  const [isHeightCollapsed, setIsHeightCollapsed] = useState(!isVisible && !preserveHiddenGeometry);
  const [hasCompletedExit, setHasCompletedExit] = useState(!isVisible);
  const [hasCompletedEnter, setHasCompletedEnter] = useState(isVisible);

  // The pills render regardless of the chat redesign, but the link opens the settings modal, whose
  // routes no-op while the redesign is off. Showing it there would be a click that does nothing.
  const {llmEnableChatRedesign} = useRolloutFlags();
  const showExploreUseCases = llmEnableChatRedesign === true;

  const usedChatUseCaseIds = useUsedChatUseCaseIds();

  // The pills are drawn at random from the categories the user hasn't used, so deal them once when
  // the intro mounts. Recomputing per render would reshuffle them under the user, and recomputing
  // when usedChatUseCaseIds changes would reshuffle them at the worst possible moment: right after a
  // click, while the panel is collapsing.
  const [categories] = useState(() => pickIntroProgressiveDisclosureCategories(usedChatUseCaseIds));
  const activeCategory = categories.find((c) => c.id === activeCategoryId) ?? null;
  // New options lead, then untried ones, then the ones already run.
  const activeCategoryOptions =
    activeCategory != null ? sortForDiscovery(activeCategory.options, usedChatUseCaseIds) : [];

  // Report the hand that was dealt so use case runs can be read against what was actually offered.
  // Waits for the fade to finish rather than for `isVisible`: the intro mounts the pills before
  // showing them, and typing during the fade hides them again, which is not a view.
  const hasTrackedViewRef = useRef(false);
  useEffect(() => {
    if (!isVisible || !hasCompletedEnter || hasTrackedViewRef.current) {
      return;
    }
    hasTrackedViewRef.current = true;
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_PROGRESSIVE_DISCLOSURE_CATEGORIES, {
      categoryIds: categories.map((category) => category.id).join(','),
      untriedCategoryCount: countUntriedCategories(usedChatUseCaseIds),
    });
  }, [categories, hasCompletedEnter, isVisible, usedChatUseCaseIds]);

  useLayoutEffect(() => {
    if (!isStacked || activeCategory != null || categoryListRef.current == null) {
      return;
    }
    const measuredHeight = categoryListRef.current.getBoundingClientRect().height;
    if (measuredHeight > 0) {
      setCategoryListMinHeight(measuredHeight);
    }
  }, [activeCategory, categories.length, isStacked]);

  useEffect(() => {
    if (isVisible) {
      setHasCompletedExit(false);
      setIsHeightCollapsed(false);
      return;
    }
    if (preserveHiddenGeometry) {
      setIsHeightCollapsed(false);
    } else if (hasCompletedExit) {
      setIsHeightCollapsed(true);
    }
  }, [hasCompletedExit, isVisible, preserveHiddenGeometry]);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  const dismissPanel = useCallback(() => {
    clearHoverTimer();
    setActiveCategoryId(null);
    onHoverOption(null);
    lastHoveredOptionRef.current = null;
  }, [onHoverOption, clearHoverTimer]);

  useEffect(() => {
    if (previousIsStackedRef.current !== isStacked) {
      previousIsStackedRef.current = isStacked;
      if (activeCategoryId != null) {
        dismissPanel();
      }
    }
  }, [activeCategoryId, dismissPanel, isStacked]);

  const containerRef = useClickOutside<HTMLDivElement>(dismissPanel);

  const handleExploreUseCases = useCallback(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_EXPLORE_USE_CASES, {source: 'progressive_disclosure'});
    navigateToURL(chatSettingsModalUrl(ChatSettingsTab.USE_CASES));
  }, []);

  const handleCategoryClick = useCallback((category: ProgressiveDisclosureCategory) => {
    setActiveCategoryId(category.id);
    lastHoveredOptionRef.current = null;
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_PROGRESSIVE_DISCLOSURE_CATEGORY, {
      categoryId: category.id,
    });
  }, []);

  const handleOptionClick = useCallback(
    (categoryId: string, optionId: string, prompt: string) => {
      clearHoverTimer();
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_PROGRESSIVE_DISCLOSURE_OPTION, {
        categoryId,
        optionId,
      });
      onHoverOption(null);
      setActiveCategoryId(null);
      // Only a submitted prompt counts as tried, so a failed chat creation doesn't sort this option
      // to the bottom for good.
      Promise.resolve(onSelectOption(prompt))
        .then((submitted) => {
          if (submitted === true) {
            recordChatUseCaseRun(optionId, {
              categoryId,
              source: 'progressive_disclosure',
              campaign: null,
            });
          }
        })
        .catch(() => {});
    },
    [onHoverOption, onSelectOption, clearHoverTimer]
  );

  const handleExitComplete = useCallback(() => {
    setHasCompletedExit(true);
    if (!preserveHiddenGeometry) {
      setIsHeightCollapsed(true);
    }
    onExitComplete?.();
  }, [onExitComplete, preserveHiddenGeometry]);

  const handleEnterStart = useCallback(() => {
    setHasCompletedEnter(false);
  }, []);

  const handleEnterComplete = useCallback(() => {
    setHasCompletedEnter(true);
  }, []);

  const handleExitStart = useCallback(() => {
    setHasCompletedEnter(false);
  }, []);

  const renderCategoryButton = (category: ProgressiveDisclosureCategory): JSX.Element => (
    <Button
      key={category.id}
      variant="ghost"
      radius="xl"
      size="sm"
      classNames={{root: classes.categoryPill, leftIcon: classes.categoryPillLeftIcon}}
      leftIcon={
        <BenchlingIcon icon={category.icon} aria-hidden={true} className={classes.categoryPillIcon} />
      }
      onClick={() => {
        handleCategoryClick(category);
      }}
    >
      {category.label}
    </Button>
  );

  /**
   * Wraps one intro item in the shared staggered fade. The last item carries the enter/exit
   * callbacks, since it is the one that finishes last and so marks the whole group done.
   */
  const renderStaggeredItem = (
    {
      itemKey,
      testProps,
      index,
      isLast,
    }: {itemKey: string; testProps: DataTestProps; index: number; isLast: boolean},
    children: React.ReactNode
  ): JSX.Element => {
    const transitionDurationMs = prefersReducedMotion
      ? 0
      : isVisible
        ? DISCLOSURE_ITEM_ENTER_MS
        : hideImmediately
          ? 0
          : DISCLOSURE_ITEM_EXIT_MS;
    const transitionDelayMs =
      (prefersReducedMotion
        ? 0
        : isVisible
          ? DISCLOSURE_ITEM_ENTER_STAGGER_MS
          : hideImmediately
            ? 0
            : DISCLOSURE_ITEM_EXIT_STAGGER_MS) * index;
    return (
      <Transition
        key={itemKey}
        keepMounted={true}
        mounted={isVisible}
        transition={{
          in: {opacity: 1, transform: 'translateY(0)'},
          out: {opacity: 0, transform: `translateY(${DISCLOSURE_ITEM_ENTER_OFFSET_PX}px)`},
          transitionProperty: 'opacity, transform',
        }}
        duration={transitionDurationMs + transitionDelayMs}
        exitDuration={transitionDurationMs + transitionDelayMs}
        timingFunction="ease"
        onEnter={isLast ? handleEnterStart : undefined}
        onEntered={isLast ? handleEnterComplete : undefined}
        onExit={isLast ? handleExitStart : undefined}
        onExited={isLast ? handleExitComplete : undefined}
      >
        {(transitionStyles) => (
          <div
            style={{
              ...preserveExitedTransitionGeometry(transitionStyles),
              ...(transitionStyles.display === 'none'
                ? {transform: `translateY(${DISCLOSURE_ITEM_ENTER_OFFSET_PX}px)`}
                : {}),
              transitionDelay: `${transitionDelayMs}ms`,
              transitionDuration: `${transitionDurationMs}ms`,
            }}
            {...testProps}
            data-amp-safe={false}
          >
            {children}
          </div>
        )}
      </Transition>
    );
  };

  // The enter/exit callbacks ride on whichever item animates last, so they move to the final pill
  // when the link is hidden; otherwise the group would never report that it finished.
  const categoryButtons = categories.map((category, index) =>
    renderStaggeredItem(
      {
        itemKey: category.id,
        testProps: getTestProps({
          component: 'ProgressiveDisclosure',
          element: 'category',
          key: category.id,
        }),
        index,
        isLast: !showExploreUseCases && index === categories.length - 1,
      },
      renderCategoryButton(category)
    )
  );

  const exploreUseCasesLink = showExploreUseCases
    ? renderStaggeredItem(
        {
          itemKey: 'explore-use-cases',
          testProps: getTestProps({component: 'ProgressiveDisclosure', element: 'exploreUseCases'}),
          index: categories.length,
          isLast: true,
        },
        <Anchor
          component="button"
          type="button"
          size="sm"
          color="blue"
          className={classes.exploreLink}
          onClick={handleExploreUseCases}
        >
          Explore more use cases
        </Anchor>
      )
    : null;

  return (
    <Collapse
      in={!isHeightCollapsed}
      animateOpacity={false}
      transitionDuration={prefersReducedMotion ? 0 : DISCLOSURE_HEIGHT_TRANSITION_MS}
      data-transition-complete={hasCompletedEnter}
      {...getTestProps({component: 'ProgressiveDisclosure', element: 'collapse'})}
    >
      <div ref={activeCategoryId != null ? containerRef : undefined} className={classes.container}>
        <Box
          style={{
            minHeight: isStacked && categoryListMinHeight > 0 ? categoryListMinHeight : undefined,
          }}
        >
          {activeCategory == null && (
            <Stack ref={categoryListRef} spacing="sm" align={isStacked ? 'flex-start' : 'center'}>
              {isStacked ? (
                <Stack spacing="sm" align="flex-start">
                  {categoryButtons}
                </Stack>
              ) : (
                <Group spacing="sm" position="center">
                  {categoryButtons}
                </Group>
              )}
              {exploreUseCasesLink}
            </Stack>
          )}
          {activeCategory != null && (
            <Transition
              keepMounted={true}
              mounted={isVisible}
              transition="fade"
              duration={prefersReducedMotion ? 0 : PROGRESSIVE_DISCLOSURE_ENTER_TRANSITION_MS}
              exitDuration={prefersReducedMotion ? 0 : PROGRESSIVE_DISCLOSURE_EXIT_TRANSITION_MS}
              onEnter={handleEnterStart}
              onEntered={handleEnterComplete}
              onExit={handleExitStart}
              onExited={handleExitComplete}
            >
              {(transitionStyles) => (
                <Paper
                  withBorder={true}
                  className={classes.optionsPanel}
                  style={preserveExitedTransitionGeometry(transitionStyles)}
                >
                  <Group position="apart" px="md" pt="sm" pb={4}>
                    <Group spacing="sm">
                      <BenchlingIcon
                        icon={activeCategory.icon}
                        aria-hidden={true}
                        className={classes.optionsPanelTitleIcon}
                      />
                      <Text size="sm" className={classes.optionsPanelTitle}>
                        {activeCategory.label}
                      </Text>
                    </Group>
                    <CloseButton size="sm" onClick={dismissPanel} aria-label="Close suggestions" />
                  </Group>
                  <Stack spacing={0} pb={0}>
                    {activeCategoryOptions.map((option, index) => (
                      <React.Fragment key={option.id}>
                        {index > 0 && <Divider className={classes.divider} />}
                        <UnstyledButton
                          className={classes.optionItem}
                          onMouseEnter={() => {
                            const key = `${activeCategory.id}:${option.id}`;
                            hoverTimerRef.current = setTimeout(() => {
                              if (lastHoveredOptionRef.current !== key) {
                                lastHoveredOptionRef.current = key;
                                trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.HOVERED_PROGRESSIVE_DISCLOSURE_OPTION, {
                                  categoryId: activeCategory.id,
                                  optionId: option.id,
                                });
                              }
                            }, 400);
                            onHoverOption(option.prompt);
                          }}
                          onMouseLeave={() => {
                            clearHoverTimer();
                            onHoverOption(null);
                          }}
                          onClick={() => {
                            handleOptionClick(activeCategory.id, option.id, option.prompt);
                          }}
                        >
                          {option.label}
                        </UnstyledButton>
                      </React.Fragment>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Transition>
          )}
        </Box>
      </div>
    </Collapse>
  );
}
